import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import React, { useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { HowToPlay, HowToPlayButton, useHowToPlay } from '@/components/games/HowToPlay';
import { ScoreShareCard, SCORE_SHARE_CARD_WIDTH } from '@/components/share/ScoreShareCard';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { ADS_ENABLED } from '@/config/ads';
import { useThemePalette } from '@/config/theme';
import {
  useDoubleScoreReward,
  useMyScorePredictions,
  useScorePredictFixtures,
  useSubmitScorePrediction,
} from '@/features/games/scorePredict';
import { useAdRewardState } from '@/features/games/rewards';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { FixtureWithRefs, ScorePrediction } from '@/lib/data/provider';
import { formatKickoffTime, formatMatchDate, formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { captureAndShare } from '@/lib/share/captureAndShare';
import { errorKey } from '@/lib/utils/errors';

const APP_LINK = 'https://play.google.com/store/apps/details?id=com.tashkeela.app';

const MAX_GOALS = 20;

function TeamSide({ team, reverse }: { team: FixtureWithRefs['home_team']; reverse?: boolean }) {
  return (
    <View className={`flex-1 flex-row items-center gap-2 ${reverse ? 'justify-end' : ''}`}>
      {reverse ? (
        <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
          {team?.short_name ?? team?.name ?? '—'}
        </AppText>
      ) : null}
      {team?.logo_url ? (
        <Image source={{ uri: team.logo_url }} style={{ width: 24, height: 24 }} contentFit="contain" cachePolicy="memory-disk" />
      ) : (
        <View className="h-6 w-6 rounded-full bg-surface-2" />
      )}
      {!reverse ? (
        <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
          {team?.short_name ?? team?.name ?? '—'}
        </AppText>
      ) : null}
    </View>
  );
}

function Stepper({ value, onChange, disabled }: { value: number; onChange: (n: number) => void; disabled?: boolean }) {
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  return (
    <View className="flex-row items-center gap-1.5">
      <Pressable
        disabled={disabled || value <= 0}
        onPress={() => onChange(value - 1)}
        hitSlop={6}
        className="h-7 w-7 items-center justify-center rounded-full bg-surface-2 active:opacity-70 disabled:opacity-30"
      >
        <Ionicons name="remove" size={16} color={palette.text} />
      </Pressable>
      <AppText weight="bold" tabular className="w-6 text-center text-ink" style={{ fontSize: 18 }}>
        {formatNumber(value, numeralOptions)}
      </AppText>
      <Pressable
        disabled={disabled || value >= MAX_GOALS}
        onPress={() => onChange(value + 1)}
        hitSlop={6}
        className="h-7 w-7 items-center justify-center rounded-full bg-surface-2 active:opacity-70 disabled:opacity-30"
      >
        <Ionicons name="add" size={16} color={palette.voltBright} />
      </Pressable>
    </View>
  );
}

function PredictCard({ fixture, saved }: { fixture: FixtureWithRefs; saved: ScorePrediction | undefined }) {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const toast = useToast();
  const submit = useSubmitScorePrediction();
  const [h, setH] = useState(saved?.home_goals ?? 0);
  const [a, setA] = useState(saved?.away_goals ?? 0);

  const dirty = saved == null || saved.home_goals !== h || saved.away_goals !== a;
  const dateRaw = fixture.kickoff_at ? new Date(fixture.kickoff_at) : null;
  const date = dateRaw && !Number.isNaN(dateRaw.getTime()) ? dateRaw : null;

  const onSave = () => {
    submit.mutate(
      { fixtureId: fixture.id, home: h, away: a },
      {
        onSuccess: () => toast.show({ message: t('scorePredict.saved'), tone: 'success' }),
        onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
      },
    );
  };

  return (
    <Card className="mx-4 mb-2.5 gap-3 px-4 py-3">
      <View className="flex-row items-center justify-center gap-1.5">
        <Ionicons name="time-outline" size={12} color="#94a3b8" />
        <AppText variant="micro" className="text-muted">
          {date ? `${formatMatchDate(date, numeralOptions)} · ${formatKickoffTime(date, numeralOptions)}` : '—'}
        </AppText>
      </View>
      <View className="flex-row items-center gap-2">
        <TeamSide team={fixture.home_team} />
        <View className="flex-row items-center gap-2">
          <Stepper value={h} onChange={setH} />
          <AppText variant="caption" className="text-muted">:</AppText>
          <Stepper value={a} onChange={setA} />
        </View>
        <TeamSide team={fixture.away_team} reverse />
      </View>
      {dirty ? (
        <Button label={t('scorePredict.save')} variant="secondary" loading={submit.isPending} onPress={onSave} />
      ) : (
        <View className="flex-row items-center justify-center gap-1.5">
          <Ionicons name="checkmark-circle" size={14} color="#34d399" />
          <AppText variant="micro" className="text-soft">{t('scorePredict.locked')}</AppText>
        </View>
      )}
    </Card>
  );
}

function ResultCard({ fixture, saved }: { fixture: FixtureWithRefs; saved: ScorePrediction }) {
  const { t } = useTranslation();
  const { numeralOptions, isRTL } = useLocale();
  const palette = useThemePalette();
  const toast = useToast();
  const dbl = useDoubleScoreReward();
  const adCapReached = (useAdRewardState().data?.remaining ?? 1) <= 0;
  const pts = saved.points ?? 0;
  const tone = pts >= 5 ? '#34d399' : pts >= 2 ? '#f5c84b' : '#94a3b8';
  const n = (x: number | null) => formatNumber(x ?? 0, numeralOptions);
  const onDouble = () =>
    dbl.mutate(saved.fixture_id, {
      onSuccess: (r) => toast.show({ message: t('scorePredict.doubledToast', { coins: n(r.reward) }), tone: 'success' }),
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });

  // "Share as image": rasterise the off-screen branded card (or fall back to
  // rich text). We re-share the exact result the user is looking at.
  const shareRef = useRef<View>(null);
  const [sharing, setSharing] = useState(false);
  const homeName = fixture.home_team?.short_name ?? fixture.home_team?.name ?? '—';
  const awayName = fixture.away_team?.short_name ?? fixture.away_team?.name ?? '—';
  const shareData = {
    homeName,
    awayName,
    homeScore: fixture.home_score ?? 0,
    awayScore: fixture.away_score ?? 0,
    pickHome: saved.home_goals,
    pickAway: saved.away_goals,
    points: pts,
  };
  const onShare = async () => {
    setSharing(true);
    try {
      const scoreText = `${homeName} ${n(fixture.home_score)}-${n(fixture.away_score)} ${awayName}`;
      const pickText = t('shareCard.shareLineYourPick', { score: `${n(saved.home_goals)}-${n(saved.away_goals)}` });
      const resultText =
        pts >= 5
          ? t('shareCard.shareLineExact')
          : t('shareCard.shareLinePoints', { pts: n(pts) });
      const message = `${scoreText}\n${pickText} · ${resultText}\n${t('common.viaApp')} ${APP_LINK}`;
      await captureAndShare({ viewRef: shareRef, message, dialogTitle: t('scorePredict.title') });
    } finally {
      setSharing(false);
    }
  };

  return (
    <Card className="mx-4 mb-2.5 gap-2 px-4 py-3">
      <View className="flex-row items-center gap-2">
        <TeamSide team={fixture.home_team} />
        <AppText weight="bold" tabular className="text-ink" style={{ fontSize: 18 }}>
          {isRTL
            ? `⁦${n(fixture.away_score)} - ${n(fixture.home_score)}⁩`
            : `⁦${n(fixture.home_score)} - ${n(fixture.away_score)}⁩`}
        </AppText>
        <TeamSide team={fixture.away_team} reverse />
      </View>
      <View className="flex-row items-center justify-between border-t border-line pt-2">
        <AppText variant="micro" className="text-muted">
          {t('scorePredict.yourPick', { score: `${n(saved.home_goals)}-${n(saved.away_goals)}` })}
        </AppText>
        <View className="flex-row items-center gap-1">
          <Ionicons name="star" size={12} color={tone} />
          <AppText variant="micro" weight="bold" tabular style={{ color: tone }}>
            {pts > 0 ? `+${n(pts)}` : n(0)} {t('scorePredict.pts')}
          </AppText>
        </View>
      </View>
      {pts > 0 && saved.doubled_at == null && ADS_ENABLED && !adCapReached ? (
        <Pressable
          accessibilityRole="button"
          disabled={dbl.isPending}
          onPress={onDouble}
          className="flex-row items-center justify-center gap-1.5 rounded-card border border-win/30 bg-win/10 py-2 active:opacity-80"
        >
          <Ionicons name={dbl.isPending ? 'hourglass-outline' : 'play-circle'} size={15} color={palette.win} />
          <AppText variant="micro" weight="bold" style={{ color: palette.win }}>
            {t('scorePredict.doubleCta')}
          </AppText>
        </Pressable>
      ) : pts > 0 && saved.doubled_at == null && ADS_ENABLED && adCapReached ? (
        <View className="flex-row items-center justify-center gap-1 py-1">
          <Ionicons name="time-outline" size={12} color={palette.inactive} />
          <AppText variant="micro" className="text-muted">
            {t('rewards.adCapReached')}
          </AppText>
        </View>
      ) : saved.doubled_at != null ? (
        <View className="flex-row items-center justify-center gap-1">
          <Ionicons name="checkmark-circle" size={13} color={palette.win} />
          <AppText variant="micro" className="text-soft">
            {t('scorePredict.doubled')}
          </AppText>
        </View>
      ) : null}

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('shareCard.shareImage')}
        disabled={sharing}
        onPress={onShare}
        className="flex-row items-center justify-center gap-1.5 rounded-card border border-volt-line bg-volt-glass py-2 active:opacity-80 disabled:opacity-50"
      >
        <Ionicons name={sharing ? 'hourglass-outline' : 'share-social'} size={14} color={palette.voltBright} />
        <AppText variant="micro" weight="bold" className="text-volt-bright">
          {t('shareCard.shareImage')}
        </AppText>
      </Pressable>

      {/* Off-screen branded card we capture to a PNG. Absolutely positioned far
          off-canvas so it never shows; `pointerEvents="none"` keeps it inert. */}
      <View
        style={{ position: 'absolute', left: -9999, top: 0, width: SCORE_SHARE_CARD_WIDTH }}
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        <ScoreShareCard ref={shareRef} data={shareData} />
      </View>
    </Card>
  );
}

export default function ScorePredictScreen() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const { userId } = useAuth();
  const router = useRouter();
  const howto = useHowToPlay('scorePredict');
  const fixturesQ = useScorePredictFixtures();
  const mineQ = useMyScorePredictions();

  const byId = useMemo(() => {
    const m = new Map<string, ScorePrediction>();
    for (const p of mineQ.data ?? []) m.set(p.fixture_id, p);
    return m;
  }, [mineQ.data]);

  const upcoming = useMemo(
    () => (fixturesQ.data ?? []).filter((f) => f.status === 'scheduled').slice(0, 30),
    [fixturesQ.data],
  );
  const results = useMemo(
    () => (fixturesQ.data ?? []).filter((f) => f.status === 'finished' && byId.has(f.id)),
    [fixturesQ.data, byId],
  );
  const totalPts = useMemo(
    () => (mineQ.data ?? []).reduce((s, p) => s + (p.points ?? 0), 0),
    [mineQ.data],
  );

  return (
    <Screen>
      <HowToPlay visible={howto.visible} onClose={howto.close} i18nKey="scorePredict" icon="football-outline" />
      <BackHeader title={t('scorePredict.title')} right={<HowToPlayButton onPress={howto.open} />} />
      {fixturesQ.isPending ? (
        <View className="gap-3 px-4 pt-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </View>
      ) : fixturesQ.isError ? (
        <ErrorState error={fixturesQ.error} onRetry={() => fixturesQ.refetch()} />
      ) : (
        <ScrollView contentContainerStyle={{ paddingBottom: 40, paddingTop: 4 }}>
          <AppText variant="caption" className="mx-4 mb-2 text-center text-soft">{t('scorePredict.intro')}</AppText>

          {userId ? (
            <Pressable
              onPress={() => router.push('/games/leaderboards-hub?tab=score' as never)}
              className="active:opacity-80"
            >
              <Card className="mx-4 mb-3 flex-row items-center gap-2 px-4 py-2.5">
                <Ionicons name="star" size={16} color={palette.captain} />
                <AppText variant="caption" weight="bold" tabular className="text-ink">
                  {formatNumber(totalPts, numeralOptions)}
                </AppText>
                <AppText variant="caption" className="text-muted">{t('scorePredict.totalPts')}</AppText>
                <View className="flex-1 flex-row items-center justify-end gap-1">
                  <Ionicons name="trophy-outline" size={14} color={palette.voltBright} />
                  <AppText variant="micro" weight="bold" className="text-volt-bright">
                    {t('scorePredict.leaderboard')}
                  </AppText>
                </View>
              </Card>
            </Pressable>
          ) : (
            <AppText variant="micro" className="mx-4 mb-2 text-center text-muted">{t('scorePredict.signIn')}</AppText>
          )}

          {upcoming.length > 0 ? (
            <>
              <AppText variant="caption" weight="bold" className="mx-4 mb-2 mt-1 uppercase tracking-wide text-soft">
                {t('scorePredict.upcoming')}
              </AppText>
              {upcoming.map((f) => {
                // Key by the saved score too: on a cold start fixtures resolve
                // before the user's saved picks, so the card first mounts at 0:0;
                // when the pick arrives the key changes and it remounts seeded
                // with the real prediction (instead of silently showing 0:0).
                const mine = byId.get(f.id);
                return (
                  <PredictCard
                    key={`${f.id}:${mine?.home_goals ?? ''}-${mine?.away_goals ?? ''}`}
                    fixture={f}
                    saved={mine}
                  />
                );
              })}
            </>
          ) : (
            <EmptyState icon="calendar-outline" title={t('scorePredict.noFixturesTitle')} body={t('scorePredict.noFixturesBody')} />
          )}

          {results.length > 0 ? (
            <>
              <AppText variant="caption" weight="bold" className="mx-4 mb-2 mt-4 uppercase tracking-wide text-soft">
                {t('scorePredict.results')}
              </AppText>
              {results.map((f) => (
                <ResultCard key={f.id} fixture={f} saved={byId.get(f.id) as ScorePrediction} />
              ))}
            </>
          ) : null}
        </ScrollView>
      )}
    </Screen>
  );
}
